import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Alert,
  Box,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider
} from '@mui/material';
import { 
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Lock as LockIcon,
  ExpandMore as ExpandMoreIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  Notifications as PushIcon,
  Schedule as ScheduleIcon,
  Campaign as CampaignIcon,
  Assessment as ReportIcon
} from '@mui/icons-material';
import { useSettings } from '../../contexts/SettingsContext';
import { apiClient } from '../../services/api';

interface NotificationSettingsData {
  email: {
    appointments: boolean;
    reminders: boolean;
    marketing: boolean;
    reports: boolean;
  };
  sms: {
    appointments: boolean;
    reminders: boolean;
    marketing: boolean;
  };
  push: {
    appointments: boolean;
    reminders: boolean;
    marketing: boolean;
  };
}

interface NotificationSettingsProps {
  salonId: string;
}

const NotificationSettings: React.FC<NotificationSettingsProps> = ({ salonId }) => {
  const [settings, setSettings] = useState<NotificationSettingsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  const { checkAccess, getRestrictions } = useSettings();
  
  const canWrite = checkAccess('notifications', 'write');
  const restrictions = getRestrictions('notifications');

  const notificationTypes = {
    appointments: {
      icon: <ScheduleIcon />,
      title: 'Appointment Notifications',
      description: 'Booking confirmations, cancellations, and updates'
    },
    reminders: {
      icon: <ScheduleIcon />,
      title: 'Appointment Reminders',
      description: 'Automated reminders before appointments'
    },
    marketing: {
      icon: <CampaignIcon />,
      title: 'Marketing Communications',
      description: 'Promotional offers and campaigns'
    },
    reports: {
      icon: <ReportIcon />,
      title: 'Business Reports',
      description: 'Daily, weekly, and monthly business reports'
    }
  };

  useEffect(() => {
    fetchSettings();
  }, [salonId]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get(`/settings/${salonId}/notifications`);
      
      if (response.success) {
        setSettings(response.data.settings);
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to load notification settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!canWrite || !settings) {
      setError('You do not have permission to modify these settings');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      
      const response = await apiClient.put(`/settings/${salonId}/notifications`, settings);
      
      if (response.success) {
        setSuccess('Notification settings updated successfully');
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to update notification settings');
    } finally {
      setSaving(false);
    }
  };

  const updateChannelSetting = (
    channel: 'email' | 'sms' | 'push',
    type: string,
    enabled: boolean
  ) => {
    if (!settings) return;

    setSettings({
      ...settings,
      [channel]: {
        ...settings[channel],
        [type]: enabled
      }
    });
  };

  const getChannelStatus = (channel: 'email' | 'sms' | 'push') => {
    if (!settings) return { total: 0, enabled: 0 };
    
    const channelSettings = settings[channel];
    const total = Object.keys(channelSettings).length;
    const enabled = Object.values(channelSettings).filter(Boolean).length;
    
    return { total, enabled };
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!settings) {
    return (
      <Alert severity="error">
        Failed to load notification settings. Please try refreshing the page.
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Notification Preferences</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {restrictions.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              {restrictions.map(restriction => (
                <Chip
                  key={restriction}
                  icon={<LockIcon />}
                  label={restriction.replace(/_/g, ' ')}
                  color="warning"
                  size="small"
                />
              ))}
            </Box>
          )}
          <Button
            startIcon={<RefreshIcon />}
            onClick={fetchSettings}
            disabled={loading}
            size="small"
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Email Notifications */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <EmailIcon color="primary" />
                <Typography variant="h6">
                  Email Notifications
                </Typography>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Chip 
                  label={`${getChannelStatus('email').enabled}/${getChannelStatus('email').total} enabled`}
                  color="primary"
                  size="small"
                />
              </Box>
              
              <List dense>
                {Object.entries(notificationTypes).map(([type, config]) => {
                  if (type === 'reports' && !settings.email.hasOwnProperty('reports')) return null;
                  
                  return (
                    <ListItem key={type}>
                      <ListItemText
                        primary={config.title}
                        secondary={config.description}
                      />
                      <ListItemSecondaryAction>
                        <Switch
                          checked={settings.email[type as keyof typeof settings.email] || false}
                          onChange={(e) => updateChannelSetting('email', type, e.target.checked)}
                          disabled={!canWrite}
                          size="small"
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                  );
                })}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* SMS Notifications */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <SmsIcon color="primary" />
                <Typography variant="h6">
                  SMS Notifications
                </Typography>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Chip 
                  label={`${getChannelStatus('sms').enabled}/${getChannelStatus('sms').total} enabled`}
                  color="primary"
                  size="small"
                />
              </Box>
              
              <List dense>
                {Object.entries(notificationTypes).map(([type, config]) => {
                  if (type === 'reports') return null; // SMS doesn't support reports
                  
                  return (
                    <ListItem key={type}>
                      <ListItemText
                        primary={config.title}
                        secondary={config.description}
                      />
                      <ListItemSecondaryAction>
                        <Switch
                          checked={settings.sms[type as keyof typeof settings.sms] || false}
                          onChange={(e) => updateChannelSetting('sms', type, e.target.checked)}
                          disabled={!canWrite}
                          size="small"
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                  );
                })}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Push Notifications */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <PushIcon color="primary" />
                <Typography variant="h6">
                  Push Notifications
                </Typography>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Chip 
                  label={`${getChannelStatus('push').enabled}/${getChannelStatus('push').total} enabled`}
                  color="primary"
                  size="small"
                />
              </Box>
              
              <List dense>
                {Object.entries(notificationTypes).map(([type, config]) => {
                  if (type === 'reports') return null; // Push doesn't support reports
                  
                  return (
                    <ListItem key={type}>
                      <ListItemText
                        primary={config.title}
                        secondary={config.description}
                      />
                      <ListItemSecondaryAction>
                        <Switch
                          checked={settings.push[type as keyof typeof settings.push] || false}
                          onChange={(e) => updateChannelSetting('push', type, e.target.checked)}
                          disabled={!canWrite}
                          size="small"
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                  );
                })}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Notification Summary */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Notification Summary
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: 'center', p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                    <EmailIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="h6">
                      {getChannelStatus('email').enabled}/{getChannelStatus('email').total}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Email Notifications
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: 'center', p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                    <SmsIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="h6">
                      {getChannelStatus('sms').enabled}/{getChannelStatus('sms').total}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      SMS Notifications
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: 'center', p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                    <PushIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="h6">
                      {getChannelStatus('push').enabled}/{getChannelStatus('push').total}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Push Notifications
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: 'center', p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                    <Typography variant="h4" color="success.main" sx={{ mb: 1 }}>
                      {Math.round(((getChannelStatus('email').enabled + getChannelStatus('sms').enabled + getChannelStatus('push').enabled) / (getChannelStatus('email').total + getChannelStatus('sms').total + getChannelStatus('push').total)) * 100)}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Overall Coverage
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Best Practices */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Notification Best Practices
              </Typography>
              
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Recommended Settings
                </Typography>
                <Typography variant="body2">
                  • Enable appointment confirmations and reminders across all channels
                  • Use SMS for urgent notifications (appointment changes, cancellations)
                  • Use email for detailed information and reports
                  • Be selective with marketing notifications to avoid spam
                </Typography>
              </Alert>
              
              <List>
                <ListItem>
                  <ListItemText
                    primary="Appointment Notifications"
                    secondary="Essential for customer communication and reducing no-shows"
                  />
                  <ListItemSecondaryAction>
                    <Chip 
                      label="Critical" 
                      color="error" 
                      size="small" 
                    />
                  </ListItemSecondaryAction>
                </ListItem>
                
                <ListItem>
                  <ListItemText
                    primary="Reminder Notifications"
                    secondary="Significantly reduces no-shows and improves customer satisfaction"
                  />
                  <ListItemSecondaryAction>
                    <Chip 
                      label="Highly Recommended" 
                      color="warning" 
                      size="small" 
                    />
                  </ListItemSecondaryAction>
                </ListItem>
                
                <ListItem>
                  <ListItemText
                    primary="Marketing Notifications"
                    secondary="Use sparingly to maintain customer engagement without spam"
                  />
                  <ListItemSecondaryAction>
                    <Chip 
                      label="Optional" 
                      color="info" 
                      size="small" 
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {canWrite && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            size="large"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default NotificationSettings;